export const STOPWORDS = new Set(
  "a an and are as at be but by for from has have he her his i if in into is it its me my no not of on or our she so than that the their them then there these they this to up us was we were what when which who will with you your".split(" ")
);

/** Unicode-aware words: keeps accents and non-Latin letters ("café" stays "café"). */
// RegExp objects (not literals) because the project targets an older ES level for literal flags.
const WORD_RE = new RegExp("[\\p{L}\\p{N}]+(?:['’][\\p{L}]+)?", "gu");
const ALNUM_RE = new RegExp("[\\p{L}\\p{N}]+", "gu");
const LATIN_NUM_RE = new RegExp("^[\\p{Script=Latin}\\p{N}]+$", "u");

export function tokenize(text: string): string[] {
  return text.toLowerCase().normalize("NFC").match(WORD_RE) ?? [];
}

export interface Freq { phrase: string; count: number; density: string }

/** Phrases never span a sentence or clause break (. ! ? , ; : at a word end, line breaks, dashes). */
const BREAK_RE = new RegExp("[.!?,;:\\u2026]+(?=\\s|$)|[\\r\\n]+|\\s[-\\u2013\\u2014]+\\s|[\\u2013\\u2014]", "u");

export function keywordFrequencies(text: string, opts: { phraseLength: 1 | 2 | 3; skipCommon: boolean; limit?: number }): { rows: Freq[]; totalWords: number } {
  const totalWords = tokenize(text).length;
  const n = opts.phraseLength;
  const counts = new Map<string, number>();
  for (const segment of text.split(BREAK_RE)) {
    const all = tokenize(segment);
    for (let i = 0; i + n <= all.length; i++) {
      const gram = all.slice(i, i + n);
      if (opts.skipCommon && (STOPWORDS.has(gram[0]) || STOPWORDS.has(gram[n - 1]))) continue;
      // Single very short tokens are noise, but one CJK/other-script character can be a whole word.
      if (n === 1 && gram[0].length < 3 && LATIN_NUM_RE.test(gram[0])) continue;
      const key = gram.join(" ");
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  const rows = Array.from(counts, ([phrase, count]) => ({ phrase, count, density: ((count / Math.max(1, totalWords)) * 100).toFixed(2) }))
    .sort((a, b) => b.count - a.count || a.phrase.localeCompare(b.phrase))
    .slice(0, opts.limit ?? 20);
  return { rows, totalWords };
}

const SPECIAL: [RegExp, string][] = [
  [/c\+\+/gi, "cplusplus"], [/c#/gi, "csharp"], [/f#/gi, "fsharp"], [/&/g, "and"], [/\.net\b/gi, "dotnet"],
];

const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

/** Hashtag-safe word: letters and digits only (Unicode kept), special cases spelled out. */
export function tagWord(w: string): string {
  let s = w;
  for (const [re, rep] of SPECIAL) s = s.replace(re, ` ${rep} `);
  return (s.match(ALNUM_RE) ?? []).join("");
}

/** Longest hashtag we output (a safe cap: very long tags are unreadable and some apps cut them). */
export const MAX_TAG_LENGTH = 100;
/** Per-post hashtag counts the platforms themselves state. Others publish no fixed limit, so none is applied. */
export const PLATFORM_LIMITS: { id: string; label: string; max: number | null; note: string }[] = [
  { id: "any", label: "Any platform", max: null, note: "No platform limit applied." },
  { id: "instagram", label: "Instagram (30 hashtags per post)", max: 30, note: "Instagram allows up to 30 hashtags per post." },
  { id: "youtube", label: "YouTube (15 hashtags per video)", max: 15, note: "YouTube ignores all hashtags on a video that has more than 15." },
  { id: "other", label: "LinkedIn, X, TikTok (no fixed limit)", max: null, note: "These apps publish no fixed hashtag count, so none is applied. A few focused tags usually read best." },
];

/** Letters without accents: "Café Crème" becomes "Cafe Creme". */
export const toAscii = (s: string) => s.normalize("NFD").replace(new RegExp("\\p{M}+", "gu"), "").replace(/ß/g, "ss").replace(/ø/gi, "o").replace(/æ/gi, "ae").replace(/œ/gi, "oe").replace(/đ/gi, "d").replace(/ł/gi, "l");

export function hashtagsFor(topic: string, max = 15, opts: { ascii?: boolean } = {}): string[] {
  let t = opts.ascii ? toAscii(topic) : topic;
  for (const [re, rep] of SPECIAL) t = t.replace(re, ` ${rep} `);
  const words = (t.match(ALNUM_RE) ?? []).map(w => w.toLowerCase());
  if (words.length === 0) return [];
  const meaningful = words.filter(w => !STOPWORDS.has(w));
  const base = meaningful.length ? meaningful : words;
  const camel = (ws: string[]) => ws.map(cap).join("");
  const tags: string[] = [];
  tags.push(camel(base));                               // #TravelPhotography
  base.forEach(w => tags.push(cap(w)));                 // each key word
  for (let i = 0; i + 1 < base.length; i++) tags.push(camel(base.slice(i, i + 2))); // adjacent pairs
  const head = cap(base[0]);
  for (const suffix of ["Tips", "Life", "Daily", "Community", "Lovers", "Ideas", "Inspiration"]) tags.push(head + suffix);
  const out: string[] = [];
  for (const t2 of tags) {
    const tag = "#" + t2.slice(0, MAX_TAG_LENGTH);
    if (t2.length >= 2 && !out.some(o => o.toLowerCase() === tag.toLowerCase())) out.push(tag);
  }
  return out.slice(0, max);
}
